import React, { useEffect, useRef, useState } from 'react'
import { Select } from '../../../ui'
import { useStore } from '../../../StateProvider'
import { equals, map } from 'ramda'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { fetchCurrencies, fetchPammDepositCurrencies } from '../../api'
import { usePammRefId, useParamsObj, usePresetValue } from '../../utils/hooks'
import { getUserValue, useGetPlatform } from '../utils'
import { CREATE_MASTER_ACCOUNT } from '../../../constants/routes'
import styles from './styles.css'

const ExternalSelectCurrency = ({ label, name, additionalInfo, showLabelAsPlaceholder }) => {
  const { t, i18n } = useTranslation()
  const [value, setValue] = useState('')
  const [fetchError, setFetchError] = useState('')
  const [currencies, setCurrencies] = useState([])
  const [isValid, setIsValid] = useState(null)
  const [{
    user,
    user: { brokerGroup, utmCampaign, deptCode, fundId },
    form: { accId }
  }, dispatch] = useStore()
  const location = useLocation()
  const userValue = getUserValue(accId, user, name)
  const platform = useGetPlatform()
  const valueRef = useRef('')

  const paramsObj = useParamsObj({ brokerGroup, utmCampaign, deptCode, fundId, search: location.search, platform })
  const pammRef = usePammRefId()

  const presetValue = (value) => {
    setValue(value)
    valueRef.current = value
    const isValid = value.length > 0
    setIsValid(isValid)
    dispatch({ type: 'setExtFieldValue', name, value })
    dispatch({ type: 'setValidationFld', name, value: isValid })
  }

  usePresetValue(name, userValue, presetValue)

  useEffect(() => {
    if (pammRef) {
      dispatch({ type: 'setValidationFld', name, value: true })
      return
    }

    const fetchFunction = location.pathname === CREATE_MASTER_ACCOUNT
      ? fetchPammDepositCurrencies
      : fetchCurrencies
    fetchFunction(paramsObj).then(
      /**
      * @param {Object[]} currencies
      */
      (currencies) => {
        const formattedCurrencyList = map(
          ({ value, text }) => ({ value, label: text }),
          currencies
        )
        setCurrencies(formattedCurrencyList)
        setFetchError('')

        if (currencies.length === 1) {
          valueRef.current = currencies[0].value
          setValue(currencies[0].value)
          dispatch({ type: 'setExtFieldValue', name, value: currencies[0].value })
          dispatch({ type: 'setValidationFld', name, value: true })
        } else {
          if (currencies.find((currency) => currency.value === valueRef.current)) {
            presetValue(valueRef.current)
          } else {
            presetValue('')
          }
        }
      }, (error) => {
        setFetchError(error.message)
        presetValue('')
      })
  }, [i18n.language, platform, pammRef])

  /**
   * @param {string} value
   */
  const getSelectValue = (value) => (equals(value, t('generatedField.choose')) ? '' : value)

  const onChange = ({ target: { value, name } }) => {
    const selectedValue = getSelectValue(value)
    setValue(selectedValue)
    valueRef.current = selectedValue
    setIsValid(!!selectedValue.length)

    dispatch({ type: 'setExtFieldValue', name, value: selectedValue })
    dispatch({ type: 'setValidationFld', name, value: !!selectedValue.length })
  }

  return (
    <>
      {currencies.length > 1 && !pammRef ? (
        <Select
          value={value || ''}
          label={t(label)}
          placeholder={t('generatedField.choose')}
          options={currencies}
          onChange={onChange}
          name={name}
          key={name}
          error=''
          valid={isValid}
          info={additionalInfo ? t(`generatedField.${name}AdditionalInfo`) : ''}
          showLabelAsPlaceholder={showLabelAsPlaceholder}
        />
      ) : null}
      {fetchError.length > 0 && <span className={styles.error}>{fetchError}</span>}
    </>
  )
}

const getExternalCurrencyFields = map(currencyField => {
  const { i18n, name, required, order } = currencyField
  return (
    <ExternalSelectCurrency
      label={i18n ? `generatedField.${i18n}` : ''}
      name={name}
      required={required}
      key={name}
      order={order}
    />
  )
})

export { ExternalSelectCurrency, getExternalCurrencyFields }
